import React, { useEffect, useState } from 'react';

import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import Spinner from '@components/common/Spinner/Spinner';
import Text from '@components/common/Text/Text';
import CircleAvatar from '@components/lpc/Navbar/CircleAvatar/CircleAvatar';
import ParticipantModal from '@components/lpc/Pot/MainModule/Activity/Participants/ParticipantModal/ParticipantModal';
import { personRoles } from '@models/PotModel/personRoles';
import { AvatarType } from '@propTypes/userTypes';
import { formatCurrency } from '@services/utils/StringService';

import css from './ParticipantCard.module.scss';

type ParticipantCardProps = {
    avatar: AvatarType;
    firstName: string;
    lastName: string;
    contribution: number;
    showContributionAmount: boolean;
    showParticipantName: boolean;
    personRole: string;
    isLoading?: boolean;
    hideContribution: boolean;
    hideName: boolean;
    potRef?: string;
    contributionDate?: string;
    orderId?: string;
    inviteID?: number;
    handleParticipantRefunded: () => void;
};

const ParticipantCard = ({
    avatar,
    firstName,
    lastName,
    contribution,
    showContributionAmount,
    personRole,
    isLoading,
    hideContribution,
    hideName,
    potRef,
    contributionDate,
    orderId,
    inviteID,
    handleParticipantRefunded,
}: ParticipantCardProps) => {
    const { t } = useTranslateLpc(['lpc-pot']);

    const [isContributionAmountVisible, setIsContributionAmountVisible] = useState(null);
    const [openParticipantModal, setOpenParticipantModal] = useState<boolean>(false);

    useEffect(() => {
        if (
            personRole === personRoles.ORGANISER ||
            (showContributionAmount && !hideContribution && personRole !== personRoles.BENEFICIARY)
        ) {
            setIsContributionAmountVisible(true);
        } else {
            setIsContributionAmountVisible(false);
        }
    }, [hideContribution, personRole, showContributionAmount]);

    return (
        <div>
            <div
                className={`${css.card} ${isLoading && css.card__spinner}`}
                onClick={() => setOpenParticipantModal(true)}
            >
                <div>
                    {isLoading ? (
                        <Spinner />
                    ) : (
                        <>
                            <CircleAvatar avatar={avatar} height={'56px'} width={'56px'} />
                            {hideName ? (
                                <Text variant={'caption_00'} color={'txt-primary'}>
                                    {t('activity.participation-anonymous')}
                                </Text>
                            ) : (
                                <Text variant={'caption_00'} color={'txt-primary'}>
                                    {firstName} {lastName}
                                </Text>
                            )}
                            <Text variant={'caption_02'} color={'brand-primary'}>
                                {isContributionAmountVisible
                                    ? formatCurrency(contribution)
                                    : t('activity.has-participated')}
                            </Text>
                        </>
                    )}
                </div>
            </div>
            <ParticipantModal
                isVisible={openParticipantModal}
                onHide={() => {
                    setOpenParticipantModal(false);
                }}
                potRef={potRef}
                personRole={personRole}
                setOpenParticipantModal={() => setOpenParticipantModal(false)}
                avatar={avatar}
                contribution={contribution}
                firstName={firstName}
                lastName={lastName}
                hideContribution={hideContribution}
                hideName={hideName}
                contributionDate={contributionDate}
                orderId={orderId}
                inviteID={inviteID}
                handleParticipantRefunded={handleParticipantRefunded}
            />
        </div>
    );
};
export default ParticipantCard;
